import { RRule } from 'rrule';
import { CourseBookingDetails, InstructorEntity, RecurringDate, RoomEntity } from '@core/entities';
import { ScheduleColumn, ScheduleItem } from '@core/types';

export interface ScheduleConversionConfig<T> {
  getColumnId: (item: T) => number | string;
  getColumnTitle: (item: T) => string;
  getBookingDetails: (item: T) => CourseBookingDetails[];
  itemIdForSchedule?: (item: T) => number | string;
  status?: string;
  includeMeta?: boolean;
}

/**
 * Convert instructors to schedule data
 */
export function convertInstructorsToScheduleData(instructors: InstructorEntity[]): {
  columns: ScheduleColumn[];
  items: ScheduleItem[];
} {
  return convertToScheduleData(instructors, {
    getColumnId: (instructor) => instructor.id,
    getColumnTitle: (instructor) => `${instructor.firstName} ${instructor.lastName}`,
    getBookingDetails: (instructor) => instructor.instructorBookingDetails || [],
    status: 'booked',
    includeMeta: true,
  });
}

/**
 * Convert rooms to schedule data
 */
export function convertRoomsToScheduleData(rooms: RoomEntity[]): {
  columns: ScheduleColumn[];
  items: ScheduleItem[];
} {
  return convertToScheduleData(rooms, {
    getColumnId: (room) => room.id,
    getColumnTitle: (room) => room.name || `Room ${room.id}`,
    getBookingDetails: (room) => room.roomBookingDetails || [],
    status: 'booked',
    includeMeta: true,
  });
}

/**
 * Generic function to convert any entity with booking details to schedule data
 */
export function convertToScheduleData<T>(items: T[], config: ScheduleConversionConfig<T>): { columns: ScheduleColumn[]; items: ScheduleItem[] } {
  // Filter out items without booking details
  const validItems = items.filter((item) => {
    const bookingDetails = config.getBookingDetails(item);
    return bookingDetails && bookingDetails.length > 0;
  });

  // Process recurring dates only for valid items
  if (validItems.length > 0) {
    populateRecurringDatesInPlace(validItems, config.getBookingDetails);
  }

  // Create columns for all items (even those without bookings)
  const columns: ScheduleColumn[] = items.map((item) => ({
    id: +config.getColumnId(item),
    title: config.getColumnTitle(item),
  }));

  // Create schedule items only from valid items
  const scheduleItems: ScheduleItem[] = validItems.flatMap((item) =>
    config.getBookingDetails(item).flatMap((bookingDetail) =>
      (bookingDetail.dates || []).map((date) => ({
        id: config.itemIdForSchedule ? +config.itemIdForSchedule(item) : +config.getColumnId(item),
        title: bookingDetail.course?.name || '',
        date: date.dateFrom,
        startTime: date.startTime,
        endTime: date.endTime,
        columnId: +config.getColumnId(item),
        status: (config.status as any) || 'booked',
        ...(config.includeMeta && { meta: item }),
      })),
    ),
  );

  return { columns, items: scheduleItems };
}

/**
 * Process a single booking detail to populate recurring dates
 */
function processBookingDetail(bookingDetail: CourseBookingDetails): CourseBookingDetails {
  const { course, dates } = bookingDetail;

  // Safety checks
  if (!course || !dates) {
    return bookingDetail;
  }

  // If no recurrence string or invalid recurrence, return as is
  if (!course.recurrence || course.recurrence === 'null' || course.recurrence === 'undefined' || course.recurrence === '') {
    return bookingDetail;
  }

  try {
    const rruleSet = RRule.fromString(course.recurrence);
    const recurringDates = rruleSet.all();

    // Get the first existing date to use as template for times
    const templateDate = dates.length > 0 ? dates[0] : null;

    if (!templateDate) {
      console.warn(`No template date found for course ${course.id}. Skipping recurrence generation.`);
      return bookingDetail;
    }

    const generatedDates: RecurringDate[] = recurringDates.map((date) => {
      const dateUtc = new Date(date.toISOString());
      const dateFrom = dateUtc.toISOString().split('T')[0];

      return {
        dateFrom,
        startTime: templateDate.startTime,
        endTime: templateDate.endTime,
        dateEnd: templateDate.dateEnd,
      };
    });

    const combinedDates = [...dates, ...generatedDates];
    const uniqueDates = removeDuplicateDates(combinedDates);
    const sortedDates = sortDatesByDateFrom(uniqueDates);

    return {
      ...bookingDetail,
      dates: sortedDates,
    };
  } catch (error) {
    console.error(`Error processing recurrence for course ${course.id}:`, error);
    return bookingDetail;
  }
}

/**
 * Remove duplicate dates based on dateFrom and startTime
 */
function removeDuplicateDates(dates: RecurringDate[]): RecurringDate[] {
  const uniqueMap = new Map<string, RecurringDate>();

  dates.forEach((date) => {
    const key = `${date.dateFrom}_${date.startTime}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, date);
    }
  });

  return Array.from(uniqueMap.values());
}

/**
 * Sort dates by dateFrom in ascending order
 */
function sortDatesByDateFrom(dates: RecurringDate[]): RecurringDate[] {
  return dates.sort((a, b) => {
    const dateA = new Date(a.dateFrom);
    const dateB = new Date(b.dateFrom);
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Process booking details in place for an array of items (instructors, rooms, etc.)
 */
function populateRecurringDatesInPlace<T>(items: T[], getBookingDetails: (item: T) => CourseBookingDetails[]): void {
  items.forEach((item) => {
    const bookingDetails = getBookingDetails(item);
    if (bookingDetails && bookingDetails.length > 0) {
      bookingDetails.forEach((bookingDetail, index) => {
        bookingDetails[index] = processBookingDetail(bookingDetail);
      });
    }
  });
}
